import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styled from 'styled-components';
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import { Download, Play } from 'lucide-react';
import ARGameScreen from '../components/ARGameScreen';

const QRPrintContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  color: white;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 15px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 40px;
  text-align: center;
`;

const QRGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 40px;
`;

const QRCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const QRCanvas = styled.canvas`
  width: 180px;
  height: 180px;
  margin: 0 auto 20px;
  border-radius: 10px;
`;

const ObjectName = styled.h3`
  color: #333;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

const ActionButton = styled.button`
  padding: 15px 40px;
  border: none;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;

  ${props => props.$primary ? `
    background: white;
    color: #667eea;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(255, 255, 255, 0.3);
    }
  ` : `
    background: rgba(255, 255, 255, 0.2);
    color: white;
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  `}
`;

export default function ARGameSoloPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quizData, setQuizData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQRPrint, setShowQRPrint] = useState(false);
  const [qrDataUrls, setQrDataUrls] = useState({});
  const qrPrintRef = useRef(null);

  // URL 파라미터 또는 state에서 게임 정보 가져오기
  const {
    nickname,
    roomCode = '',
    userRank = 1,
    initialScore = 0,
    problemSet,
    mode = 'qr-scan' // 기본값: QR 스캔 모드
  } = location.state || {};

  // 닉네임 우선 사용: location.state의 nickname > user의 username > 기본값
  const displayName = nickname || user?.username || '플레이어';

  // 퀴즈 데이터 로드
  useEffect(() => {
    const loadQuizData = async () => {
      const setId = problemSet?.id || problemSet?.set_id;

      if (!setId) {
        console.error('퀴즈 ID가 없습니다. problemSet:', problemSet);
        setLoading(false);
        return;
      }

      console.log('📝 퀴즈 로드 시작 - set_id:', setId);

      try {
        const response = await fetch(`/auth/get-quiz-detail/${setId}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          console.log('✅ 퀴즈 데이터 로드 성공:', data);

          // 솔로 플레이 시작 - 플레이 카운트 증가
          try {
            const playCountResponse = await fetch(`/auth/increment-play-count/${setId}`, {
              method: 'POST',
              credentials: 'include'
            });
            const playCountData = await playCountResponse.json();
            if (playCountData.success) {
              console.log(`✅ 플레이 카운트 증가: ${playCountData.newPlayCount}회`);
            }
          } catch (error) {
            console.error('⚠️ 플레이 카운트 증가 실패:', error);
            // 플레이 카운트 증가 실패해도 게임은 계속 진행
          }

          // 데이터 구조 변환: AR 객체별로 문제 그룹화
          const problemsByObject = {};

          data.quiz.problems.forEach(problem => {
            const objectId = problem.ar_object_id;
            const objectName = problem.ar_object_name || problem.arObject || '알 수 없는 객체';
            const objectImage = problem.ar_object_image || problem.arObjectImage || '/ThumbnailEx1.jpg';

            console.log(`📦 AR 객체 처리: ID=${objectId}, Name=${objectName}, Image=${objectImage}`);

            if (!problemsByObject[objectId]) {
              problemsByObject[objectId] = {
                ar_object_id: objectId,
                ar_object_name: objectName,
                ar_object_image: objectImage,
                ar_object_model: problem.model_url || null, // GLB 모델 URL 추가
                questions: []
              };
            }

            // API는 이미 choices 배열로 반환함
            const choices = problem.choices || [
              problem.answer_text1,
              problem.answer_text2,
              problem.answer_text3,
              problem.answer_text4
            ].filter(Boolean);

            console.log(`  📝 문제: ${problem.question_text?.substring(0, 30)}...`);
            console.log(`  📋 선택지 (${choices.length}개):`, choices);
            console.log(`  ✅ 정답: ${problem.correct_answer || problem.correctAnswer}`);

            problemsByObject[objectId].questions.push({
              question_id: problem.question_id,
              question_text: problem.question_text,
              question_timelimit: problem.question_timelimit || problem.timelimit,
              choices: choices,
              correct_answer: problem.correct_answer || problem.correctAnswer // 0-based index
            });
          });

          const quizTitle = data.quiz.set_title || '퀴즈';
          console.log('✅ 퀴즈 제목:', quizTitle);
          console.log('✅ AR 객체 수:', Object.keys(problemsByObject).length);

          setQuizData({
            set_id: setId,
            title: quizTitle,
            objects: Object.values(problemsByObject)
          });

          // Solo Play 초기 레코드는 첫 퀴즈 완료 시 자동 생성되므로 여기서는 생성하지 않음
          console.log('✅ Solo Play 준비 완료 - 첫 퀴즈 완료 시 자동으로 레코드 생성됩니다.');
        } else {
          console.error('퀴즈 데이터 로드 실패:', data.error);
        }
      } catch (error) {
        console.error('퀴즈 데이터 로드 중 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    loadQuizData();
  }, [problemSet?.id]);

  // QR 코드 생성
  useEffect(() => {
    if (quizData && mode === 'qr-scan') {
      const generateQRCodes = async () => {
        const qrUrls = {};
        for (const obj of quizData.objects) {
          try {
            // QR 코드 데이터: AR 객체 ID를 인코딩
            const qrData = JSON.stringify({
              ar_object_id: obj.ar_object_id,
              ar_object_name: obj.ar_object_name,
              set_id: quizData.set_id
            });

            const dataUrl = await QRCode.toDataURL(qrData, {
              width: 300,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });

            qrUrls[obj.ar_object_id] = dataUrl;
          } catch (error) {
            console.error('QR 코드 생성 실패:', error);
          }
        }
        setQrDataUrls(qrUrls);
        setShowQRPrint(true);
      };

      generateQRCodes();
    }
  }, [quizData, mode]);

  // QR 코드 다운로드
  const handleDownloadQRCodes = async () => {
    if (!qrPrintRef.current) return;

    try {
      const canvas = await html2canvas(qrPrintRef.current, {
        backgroundColor: '#667eea',
        scale: 2
      });

      const link = document.createElement('a');
      link.download = `AR_QR_Codes_${quizData.title}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('QR 코드 다운로드 실패:', error);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'black',
        color: 'white',
        fontSize: '18px'
      }}>
        퀴즈 데이터 로딩 중...
      </div>
    );
  }

  if (!quizData) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'black',
        color: 'white',
        fontSize: '18px'
      }}>
        퀴즈 데이터를 불러올 수 없습니다.
      </div>
    );
  }

  // QR 스캔 모드이고 QR 인쇄 화면을 보여줘야 할 때
  if (mode === 'qr-scan' && showQRPrint) {
    return (
      <QRPrintContainer ref={qrPrintRef}>
        <Title>🎯 AR 객체 QR 코드</Title>
        <Subtitle>각 QR 코드를 인쇄하거나 화면에 띄워두고 게임을 시작하세요</Subtitle>

        <QRGrid>
          {quizData.objects.map((obj) => (
            <QRCard key={obj.ar_object_id}>
              {qrDataUrls[obj.ar_object_id] && (
                <img
                  src={qrDataUrls[obj.ar_object_id]}
                  alt={`${obj.ar_object_name} QR`}
                  style={{ width: '180px', height: '180px', margin: '0 auto 20px', borderRadius: '10px' }}
                />
              )}
              <ObjectName>{obj.ar_object_name}</ObjectName>
            </QRCard>
          ))}
        </QRGrid>

        <ButtonContainer>
          <ActionButton onClick={handleDownloadQRCodes}>
            <Download size={20} />
            QR 코드 다운로드
          </ActionButton>
          <ActionButton $primary onClick={() => setShowQRPrint(false)}>
            <Play size={20} />
            게임 시작
          </ActionButton>
        </ButtonContainer>
      </QRPrintContainer>
    );
  }

  return (
    <ARGameScreen
      userName={displayName}
      userRank={userRank}
      initialScore={initialScore}
      roomCode={roomCode}
      isSoloMode={true}
      quizData={quizData} // 퀴즈 데이터 전달
      gameMode={mode} // 게임 모드 전달 (qr-scan 또는 normal)
    />
  );
}
